//#region Imports

import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CourseCardComponent } from '../../shared/components/course-card/course-card.component';
import { Course } from '../../shared/models/interfaces/course.interface';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { RouterModule } from '@angular/router';
import { CourseService } from '../../shared/services/course/course.service';
import { HomeFilterComponent } from './filter/filter.component';
import { HomeFilter } from '../../shared/models/interfaces/home-filter.interface';
import { MeService } from '../../shared/services/me/me.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { Category } from '../../shared/models/interfaces/category.interface';
import { CoursesByCategory } from '../../shared/models/interfaces/courses-by-category.interface';
import { CategoryService } from '../../shared/services/category/category.service';
import { CourseType } from '../../shared/models/enums/course-type.enum';
import { CourseLocation } from '../../shared/models/enums/course-location.enum';
import { getCrudErrors } from '../../shared/utils/functions';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';

//#endregion

@Component({
  selector: 'l-home',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CourseCardComponent,
    CarouselComponent,
    RouterModule,
    FormsModule,
    HomeFilterComponent,
    SpinnerComponent,
  ],
  templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit{ 

  //#region Constructor

  constructor(
    private readonly categoryService: CategoryService,
    private readonly toastService: ToastService,
    private readonly courseService: CourseService,
    private readonly authService: AuthService,
    private readonly meService: MeService,
  ) {}

  //#endregion

  //#region Public Properties

  public courses: Course[] = [];

  public isLoadingCourses: boolean = false;

  public isLoadingCategorySections: boolean = false;

  public categories: Category[] = [];

  public categorySections: CoursesByCategory[] = [];


  public searchTerm: string = '';

  public userId: string = '';

  public selectedCategories: Category[] = [];

  public selectedType: CourseType | null = null;

  public selectedLocation: CourseLocation | null = null;

  //#endregion

  //#region Public Methods

  public ngOnInit(): void {
    void this.initializeUser().then(async () => {
      if (!this.userId) return;

      void Promise.all([
        this.getRecommendedCourses(),
        this.loadCategories(),
      ]);
    });
  }

  public async applyFilters(filter: HomeFilter): Promise<void> {
    this.selectedCategories = filter.categories ?? [];
    this.selectedType = filter.type;
    this.selectedLocation = filter.location;
    this.searchTerm = filter.searchTerm;
    await this.loadCoursesByCategory();
  }

  //#endregion

  //#region Private Methods

  private async initializeUser(): Promise<void> {
    this.userId = this.meService.getMe()?.id ?? '';

    if (this.userId) return;

    await this.meService.storageMe();
    this.userId = this.meService.getMe()?.id ?? '';

    if (!this.userId && this.authService.isAccessTokenExpired()) {
      await this.authService.logout();
    }
  }

  private async getRecommendedCourses(): Promise<void> {
    try {
      this.isLoadingCourses = true;

      const [recommended, enrollments] = await Promise.all([
        this.courseService.getRecommended(),
        this.courseService.getMyEnrollments(),
      ]);

      const progressById = new Map(enrollments.map(e => [e.courseId, e.progress]));

      this.courses = recommended.map(course => ({
        ...course,
        progress: progressById.get(course.id) ?? 0,
      }));
    } catch (error) {
      this.courses = [];
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    } finally {
      this.isLoadingCourses = false;
    }
  }

  private async loadCategories(): Promise<void> {
    try {
      this.categories = await this.categoryService.search({
        search: { isActive: true },
        sort: [{ field: 'name', order: 'ASC' }]
      });

      await this.loadCoursesByCategory();
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    }
  }

  private async loadCoursesByCategory(): Promise<void> {
    try {
      this.isLoadingCategorySections = true;
      if (!this.categories.length) {
        this.categorySections = [];
        return;
      }

      const groupedByCategory = await this.courseService.getCoursesGroupedByCategory({
        categoryIds: this.selectedCategories.map(category => category.id),
        type: this.selectedType,
        location: this.selectedLocation,
        searchTerm: this.searchTerm,
      });

      this.categorySections = groupedByCategory;
    } catch (error) {
      this.toastService.error(getCrudErrors(error)[0], 'Oops...');
    } finally {
      this.isLoadingCategorySections = false;
    }
  }

  //#endregion
}
